import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { stepHistory, type StepRecord } from "../data/stepHistory";

const DAY_IN_MS = 86_400_000;
const TARGET_STEPS = 10_000;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function toDateKey(date: Date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, amount: number) {
  return new Date(date.getTime() + amount * DAY_IN_MS);
}

function toUtcDate(date: Date) {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

function getLevel(steps: number) {
  if (steps === 0) return 0;
  if (steps < 5_000) return 1;
  if (steps < 8_000) return 2;
  if (steps < TARGET_STEPS) return 3;
  return 4;
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStepRecord(value: unknown): value is StepRecord {
  return (
    isObject(value) &&
    typeof value.date === "string" &&
    DATE_PATTERN.test(value.date) &&
    typeof value.steps === "number" &&
    Number.isInteger(value.steps) &&
    value.steps >= 0
  );
}

function isStepsResponse(value: unknown): value is { records: StepRecord[] } {
  return isObject(value) && Array.isArray(value.records) && value.records.every(isStepRecord);
}

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function getTooltipPosition(button: HTMLButtonElement) {
  const rect = button.getBoundingClientRect();
  const center = rect.left + rect.width / 2;
  const left = Math.max(8, Math.min(center - 72, window.innerWidth - 152));
  const below = rect.top < 64;
  return { left, top: below ? rect.bottom + 8 : rect.top - 8, below, point: center - left };
}

export function StepsTracker() {
  const today = toUtcDate(new Date());
  const year = today.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const yearEnd = new Date(Date.UTC(year, 11, 31));
  const [remoteRecords, setRemoteRecords] = useState<StepRecord[] | null>(null);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [tooltipDate, setTooltipDate] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0, below: false, point: 72 });
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();

    async function loadSteps() {
      try {
        const response = await fetch("/api/steps?days=366", {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Step API returned ${response.status}`);

        const value: unknown = await response.json();
        if (!isStepsResponse(value)) throw new Error("Step API returned an invalid response");

        setRemoteRecords(value.records);
      } catch {
        if (controller.signal.aborted) return;
      }
    }

    void loadSteps();
    return () => controller.abort();
  }, []);

  const sourceRecords = remoteRecords?.length ? remoteRecords : stepHistory;
  const records = sourceRecords.filter(
    record => record.date >= toDateKey(yearStart) && record.date <= toDateKey(today),
  );
  const recordedDates = records.map(record => record.date).sort();
  const latestDate = recordedDates.at(-1);

  useEffect(() => {
    const scroll = scrollRef.current;
    const grid = scroll?.querySelector<HTMLElement>(".steps-grid");
    if (!scroll || !grid || !latestDate) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let isVisible = false;
    const updateMotion = () => {
      grid.dataset.motion = isVisible && !document.hidden && !reducedMotion.matches
        ? "running"
        : "paused";
    };
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      isVisible = entry.isIntersecting;
      updateMotion();
    });

    observer.observe(scroll);
    reducedMotion.addEventListener("change", updateMotion);
    document.addEventListener("visibilitychange", updateMotion);

    return () => {
      observer.disconnect();
      grid.dataset.motion = "paused";
      reducedMotion.removeEventListener("change", updateMotion);
      document.removeEventListener("visibilitychange", updateMotion);
    };
  }, [latestDate]);

  useEffect(() => {
    if (!tooltipDate) return;
    const updatePosition = () => {
      const scroll = scrollRef.current;
      const button = scroll?.querySelector<HTMLButtonElement>(`[data-date="${tooltipDate}"]`);
      if (!scroll || !button) return;
      const rect = button.getBoundingClientRect();
      const bounds = scroll.getBoundingClientRect();
      if (rect.right <= bounds.left || rect.left >= bounds.right || rect.bottom < 0 || rect.top > window.innerHeight) {
        setTooltipDate(null);
      } else {
        setTooltipPosition(getTooltipPosition(button));
      }
    };
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [tooltipDate]);

  if (records.length === 0) return null;

  const stepByDate = new Map(records.map(record => [record.date, record.steps]));
  const tabStopDate = activeDate && stepByDate.has(activeDate) ? activeDate : recordedDates[0];
  // Each column holds seven consecutive days, starting with January 1.
  const dayCount = Math.round((yearEnd.getTime() - yearStart.getTime()) / DAY_IN_MS) + 1;
  const days = Array.from({ length: dayCount }, (_, index) => addDays(yearStart, index));
  const weekCount = Math.ceil(dayCount / 7);

  const totalSteps = records.reduce((total, record) => total + record.steps, 0);
  const averageSteps = records.length > 0 ? Math.round(totalSteps / records.length) : 0;
  const goalDays = records.filter(record => record.steps >= TARGET_STEPS).length;

  function showTooltip(button: HTMLButtonElement, date: string) {
    setTooltipPosition(getTooltipPosition(button));
    setTooltipDate(date);
  }

  function handleDayKeyDown(event: KeyboardEvent<HTMLButtonElement>, date: Date) {
    if (event.key === "Escape") {
      setTooltipDate(null);
      return;
    }

    const offsets: Record<string, number> = { ArrowLeft: -7, ArrowRight: 7, ArrowUp: -1, ArrowDown: 1 };
    const offset = offsets[event.key];
    let nextDate: string | undefined;

    if (event.key === "Home") nextDate = recordedDates[0];
    else if (event.key === "End") nextDate = latestDate;
    else if (offset !== undefined) {
      const target = toDateKey(addDays(date, offset));
      nextDate = offset > 0
        ? recordedDates.find(recordedDate => recordedDate >= target) ?? latestDate
        : recordedDates.findLast(recordedDate => recordedDate <= target) ?? recordedDates[0];
    } else return;

    event.preventDefault();
    scrollRef.current?.querySelector<HTMLButtonElement>(`[data-date="${nextDate}"]`)?.focus();
  }

  const monthLabels = days
    .filter(date => date.getUTCDate() === 1)
    .map(date => ({
      label: date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }),
      week: Math.floor((date.getTime() - yearStart.getTime()) / DAY_IN_MS / 7) + 1,
    }));

  return (
    <section
      className="steps-tracker"
      aria-labelledby="steps-tracker-title"
      style={{ "--steps-weeks": weekCount } as CSSProperties}
    >
      <div className="steps-heading">
        <h2 id="steps-tracker-title" className="steps-title">
          <span className="steps-correction">
            <span className="steps-note">
              <span className="steps-handwritten">Steps</span>
              <svg className="steps-correction-arrow" viewBox="0 0 62 30" fill="none" aria-hidden="true">
                <path d="M3 5C10 20 29 25 56 19M49 14L57 19L50 25" />
              </svg>
            </span>
          </span>
        </h2>
      </div>

      <p id="steps-keyboard-help" className="sr-only">
        Use the arrow keys to explore days, Home and End to jump to the first or last recorded day,
        and Escape to dismiss the tooltip.
      </p>
      <div className="steps-scroll" ref={scrollRef}>
        <div className="steps-chart">
          <div
            aria-hidden="true"
            className="steps-months"
          >
            {monthLabels.map(month => (
              <span key={`${month.label}-${month.week}`} style={{ gridColumnStart: month.week }}>
                {month.label}
              </span>
            ))}
          </div>

          <div
            className="steps-grid"
            role="group"
            aria-label={`${formatNumber(totalSteps)} steps in ${year} across ${records.length} ${records.length === 1 ? "day" : "days"}, averaging ${formatNumber(averageSteps)} per day, with ${goalDays} ${goalDays === 1 ? "day" : "days"} at or above 10,000 steps.`}
            aria-describedby="steps-keyboard-help"
          >
            {days.map((date, index) => {
              const dateKey = toDateKey(date);
              const isFuture = date > today;
              const syncedSteps = stepByDate.get(dateKey);
              const steps = syncedSteps ?? 0;
              const formattedDate = dateFormatter.format(date);

              if (!isFuture && syncedSteps !== undefined) {
                const tooltipId = `steps-tooltip-${dateKey}`;

                return (
                  <span
                    key={dateKey}
                    className="t-tt-wrap steps-cell-wrap"
                    data-tooltip-open={tooltipDate === dateKey}
                  >
                    <button
                      type="button"
                      aria-label={`${formattedDate}: ${formatNumber(steps)} steps`}
                      aria-describedby={tooltipDate === dateKey ? tooltipId : undefined}
                      className="steps-cell t-tt-trigger"
                      data-level={getLevel(steps)}
                      data-date={dateKey}
                      style={{ "--steps-shimmer-delay": `${Math.floor(index / 7) * 45 + (index % 7) * 12}ms` } as CSSProperties}
                      tabIndex={dateKey === tabStopDate ? 0 : -1}
                      onFocus={event => {
                        setActiveDate(dateKey);
                        showTooltip(event.currentTarget, dateKey);
                      }}
                      onBlur={() => setTooltipDate(null)}
                      onPointerEnter={event => {
                        if (event.pointerType === "mouse") showTooltip(event.currentTarget, dateKey);
                      }}
                      onPointerLeave={event => {
                        if (document.activeElement !== event.currentTarget) setTooltipDate(null);
                      }}
                      onClick={event => showTooltip(event.currentTarget, dateKey)}
                      onKeyDown={event => handleDayKeyDown(event, date)}
                    >
                      <svg className="steps-cell-surface" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                        <rect width="12" height="12" rx="1" />
                      </svg>
                    </button>
                  </span>
                );
              }

              return (
                <span
                  key={dateKey}
                  aria-hidden="true"
                  className="steps-cell"
                  data-level={isFuture ? "future" : getLevel(steps)}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="steps-footer">
        <p>{formatNumber(totalSteps)} steps in {year}</p>
      </div>
      <p className="steps-scroll-hint" aria-hidden="true">Scroll to explore the year <span>↔</span></p>
      {tooltipDate && createPortal(
        <div
          className="steps-tooltip"
          id={`steps-tooltip-${tooltipDate}`}
          role="tooltip"
          data-below={tooltipPosition.below}
          style={{
            left: tooltipPosition.left,
            top: tooltipPosition.top,
            "--tooltip-point": `${tooltipPosition.point}px`,
          } as CSSProperties}
        >
          <span>{dateFormatter.format(new Date(`${tooltipDate}T00:00:00Z`))}</span>
          <strong>{formatNumber(stepByDate.get(tooltipDate) ?? 0)} steps</strong>
        </div>,
        document.body,
      )}
    </section>
  );
}
